import React from "react";
import { useIntl } from "react-intl";

import { Attachment } from "lib/build/attachments";
import { Event, mapEventsToAttachments } from "lib/build/events";
import { subjectMessages } from "lib/build/forms/htx/patientCondition";

import { TimelineItem } from "src/common/components/Timeline";
import { useFormatMessageMap } from "src/common/hooks/useFormatMessageMap";
import { useFormatName } from "src/common/hooks/useFormatName";
import { useKnownUsers } from "src/features/auth";
import { genericMessages } from "src/messages/generic";
import { longStatusMessages } from "src/modules/htx/messages";
import {
	PatientConditionReportValue,
	PatientConditionValue
} from "src/modules/htx/status-history/types";

export function usePatientConditionTimelineItems(
	events: Event<PatientConditionValue | PatientConditionReportValue>[]
): TimelineItem<{
	attachments: Attachment[];
	data: PatientConditionValue | PatientConditionReportValue;
}>[] {
	const users = useKnownUsers();
	const { formatMessage } = useIntl();
	const formatMessageMap = useFormatMessageMap();
	const formatName = useFormatName();
	return React.useMemo(
		() =>
			events.map(event => {
				const {
					timestamp,
					value,
					origin: { recordedBy: userId }
				} = event;
				const attachments = mapEventsToAttachments(event);
				const label =
					"subject" in value
						? `${formatMessageMap(
								subjectMessages,
								value.subject
							)} - ${formatMessage(
								genericMessages.status
							)}: ${formatMessageMap(
								longStatusMessages,
								value.status
							)}`
						: formatMessage(
								genericMessages.medicalPractitionerComment
							);
				let author: string | undefined;
				if (userId in users) {
					author = formatName(users[userId]);
				}
				return {
					label,
					timestamp,
					author,
					value: { attachments, data: value }
				};
			}),
		[events, users, formatMessage, formatMessageMap, formatName]
	);
}
